import { Box, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import AnalyticsCard from "./AnalyticsCard";
import axiosInstance from "../utils/axiosConfig";
import { showErrorToast } from "../utils/toastUtils";
import banknoteIcon from "../assets/summary/Banknote.svg";
import carIcon from "../assets/summary/carIcon.svg";
import userIcon from "../assets/summary/userIcon.svg";
import ticketsIcon from "../assets/customerAnalytics/tickestIcon.svg";
import CircularLoader from "./CircularLoader";
import { SelectOptions, initialDateFilter } from "../const/analytics";
import CommonDateFilterPanel from "./CommonDateFilterPanel";
import { formatDateWithDayJs } from "../utils/formateDate";

const InsuranceAnalytics = () => {
  const [statsData, setStatsData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [dateFilter, setDateFilter] = useState(initialDateFilter);

  const handleDateFilter = (name, value) => {
    if (name === "select" && value !== "CUSTOM") {
      setDateFilter((prev) => ({
        ...prev,
        [name]: value,
        from_time: null,
        to_time: null,
      }));
      return;
    }

    setDateFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getStats = async () => {
    const queryBuilder = () => {
      const querys = [];
      const format = "YYYY-MM-DD"; // Backend expects ISO date usually

      if (
        dateFilter?.select === "CUSTOM" &&
        dateFilter?.from_time &&
        dateFilter?.to_time
      ) {
        const custom_start_date = formatDateWithDayJs(
          dateFilter?.from_time,
          format
        );
        const custom_end_date = formatDateWithDayJs(
          dateFilter?.to_time,
          format
        );

        querys.push(`from_date=${custom_start_date}`);
        querys.push(`to_date=${custom_end_date}`);
      }
      // Note: Endpoint doesn't support named ranges like 1D, 1W directly 
      // based on docs, but typically we might handle it or just rely on custom dates.
      // If the backend doesn't support 'range', we only pass dates.

      return querys.length > 0 ? `?${querys.join("&")}` : "";
    };

    setLoader(true);
    const querys = queryBuilder();
    let url = `/crm/insurance/stats/${querys}`;

    try {
      let response = await axiosInstance.get(url);
      if (response && response.data && response.data.data) {
        setStatsData(response.data.data);
      }
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    // calling summary function
    if (dateFilter?.select !== "CUSTOM") {
      // For now, if not custom, maybe we just fetch all or we'd need to map select to dates.
      // The backend docs only mention from_date and to_date. 
      // If we don't pass them, it fetches all-time stats.
      getStats();
    } else if (dateFilter?.from_time && dateFilter?.to_time) {
      getStats();
    }
  }, [dateFilter]);

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    gap: "15px",
    position: "relative",
    paddingBottom: "10px",
    margin: "20px",
  };

  const flexContainer = {
    display: "flex",
    flexDirection: "row",
    flexWrap: { xs: "wrap", lg: "nowrap" },
    boxSizing: "border-box",
    gap: "20px",
    marginTop: "20px",
  };

  const flexChildContainer = {
    width: { xs: "100%", lg: "49%" },
    flexWrap: "wrap",
  };

  return loader ? (
    <CircularLoader />
  ) : (
    <Box sx={containerStyle}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          margin: "14px 0",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "24px", fontWeight: "bold" }}>
          Insurance Analytics
        </Typography>
        <CommonDateFilterPanel
          dateFilter={dateFilter}
          filterSelectOptions={SelectOptions}
          handleDateFilter={handleDateFilter}
        />
      </Box>

      {statsData && statsData.totals && (
        <>
          <Box sx={flexContainer}>
            <Box sx={flexChildContainer}>
              <Stack
                direction={{ xs: "column", lg: "row" }}
                gap={"25px"}
                width={"100%"}
              >
                <AnalyticsCard
                  headerText={"Total Opted In"}
                  svg={userIcon}
                  value={statsData.totals.total_opted_in ?? 0}
                />
                <AnalyticsCard
                  headerText={"Coverage Total"}
                  svg={banknoteIcon}
                  value={
                    statsData.totals.coverage_total
                      ? `₱ ${statsData.totals.coverage_total}`
                      : "₱ 0"
                  }
                  format={true}
                />
              </Stack>
            </Box>
            <Box sx={flexChildContainer}>
              <Stack direction={{ xs: "column", sm: "row" }} gap={"25px"}>
                <AnalyticsCard
                  headerText={"Rider Charged Total"}
                  svg={banknoteIcon}
                  value={
                    statsData.totals.rider_charged_total
                      ? `₱ ${statsData.totals.rider_charged_total}`
                      : "₱ 0"
                  }
                  format={true}
                />
                <AnalyticsCard
                  headerText={"Stere Owed Total"}
                  svg={banknoteIcon}
                  value={
                    statsData.totals.stere_owed_total
                      ? `₱ ${statsData.totals.stere_owed_total}`
                      : "₱ 0"
                  }
                  format={true}
                />
              </Stack>
            </Box>
          </Box>

          <Box sx={flexContainer}>
            <Box sx={flexChildContainer}>
              <Stack direction={{ xs: "column", sm: "row" }} gap={"25px"}>
                <AnalyticsCard
                  headerText={"Platform Fee Earned"}
                  svg={banknoteIcon}
                  value={
                    statsData.totals.platform_fee_earned
                      ? `₱ ${statsData.totals.platform_fee_earned}`
                      : "₱ 0"
                  }
                  format={true}
                />
                <AnalyticsCard
                  headerText={"In Flight"}
                  svg={carIcon}
                  value={statsData.totals.in_flight ?? 0}
                />
              </Stack>
            </Box>
            <Box sx={flexChildContainer}>
              <Stack direction={{ xs: "column", sm: "row" }} gap={"25px"}>
                <AnalyticsCard
                  headerText={"Actionable"}
                  svg={ticketsIcon}
                  value={statsData.totals.actionable ?? 0}
                />
                <AnalyticsCard
                  headerText={"Bound Pending Invoice"}
                  svg={ticketsIcon}
                  value={statsData.totals.bound_pending_invoice ?? 0}
                />
              </Stack>
            </Box>
          </Box>

          <Typography sx={{ fontSize: "20px", fontWeight: "600", mt: "30px" }}>
            By Status Breakdown
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: "20px",
              mt: "15px",
            }}
          >
            {statsData.by_status &&
              Object.entries(statsData.by_status).map(([status, details]) => {
                let color = "#6F747E";
                let bgColor = "rgba(111, 116, 126, 0.10)";

                if (status === "BOUND") {
                  color = "#009D0F";
                  bgColor = "rgba(0, 157, 15, 0.09)";
                } else if (status === "FAILED") {
                  color = "#F1554C";
                  bgColor = "rgba(241, 85, 76, 0.08)";
                } else if (status === "QUOTED" || status === "PENDING_QUOTE") {
                  color = "#F98244";
                  bgColor = "rgba(249, 130, 68, 0.09)";
                }

                return (
                  <Box
                    key={status}
                    sx={{
                      p: 2,
                      border: "1px solid #EAEAEA",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      boxShadow: "0px 2px 4px rgba(0,0,0,0.02)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: color,
                        backgroundColor: bgColor,
                        display: "inline-block",
                        px: 1,
                        py: 0.5,
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        mb: 2,
                      }}
                    >
                      {status.replace(/_/g, " ")}
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontSize="14px" color="textSecondary">Count</Typography>
                        <Typography fontSize="14px" fontWeight="bold">{details.count}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontSize="14px" color="textSecondary">Stere Owed</Typography>
                        <Typography fontSize="14px" fontWeight="bold">₱ {details.stere_owed}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontSize="14px" color="textSecondary">Rider Charged</Typography>
                        <Typography fontSize="14px" fontWeight="bold">₱ {details.rider_charged}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography fontSize="14px" color="textSecondary">Platform Fee</Typography>
                        <Typography fontSize="14px" fontWeight="bold">₱ {details.platform_fee}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default InsuranceAnalytics;
